const _ = require("lodash");
const setIn = require("../ImmHelpers").setIn;

const settings = {
	allowRepeatTrials: true,
	staticMaxStars: true,
	neverShowSliderMessages: true,
	neverSetOutcome: true,
	noUndoRedoClear: true,
	saveAllProblems: true,
	neverResumeResult: true,
	canFinishOnStateChange: true,
	pauseWhenFailing: true,
	updatePercentCompleteOnStateChange: true,
	canEarnStarsOnStateChange: true, //Stars can be earned after the submission & animation completes
	canEarnStarsOnTrialSubmission: true,
	noProblemHistory: true,
	skipProblemCount: true,
};

const initialize = function(result, processor) {
	const currentProblem = result.problems[0];
	currentProblem.customState = processor.setupCustomState(
		currentProblem.model,
		currentProblem.customState
	);
};

const computeBackendProgress = function(result, defaultProgress) {
	return _.extend({}, defaultProgress, {
		problems: defaultProgress.problems.map(p =>
			setIn(
				p,
				"customState",
				_.omit(p.customState, [
					"solutionBatex",
					"trialCorrects",
					"prospectiveWin",
				])
			)
		),
	});
};

const computeBackendTrial = function(result, problem, backendTrial) {
	const problemType = problem.model.problemType;
	const trialData = backendTrial.response.response;
	let partialTrial = {};

	if (problemType === "divisionEstimation") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {
				terms: trialData[0],
				choices: trialData[1],
				op: trialData[2],
			},
		};
	} else if (problemType === "compareToZero") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {terms: trialData[0], op: trialData[2]},
		};
	} else if (problemType === "greaterOrLess") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {lhs: trialData[0], rhs: trialData[1], op: trialData[2]},
		};
	} else if (problemType === "quickChecks") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {
				terms: trialData[0],
				choices: trialData[1],
				op: trialData[2],
			},
		};
	} else if (problemType === "smallestOrLargest") {
		partialTrial = {
			response: {response: trialData[2]},
			problemData: {
				choices: trialData[0],
				goal: trialData[1],
			},
		};
	} else if (problemType === "countDicePips") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {
				dice: trialData[0],
				choices: trialData[1],
				op: trialData[2],
			},
		};
	} else if (problemType === "compareNumbers") {
		partialTrial = {
			response: {response: trialData[3]},
			problemData: {terms: trialData[0], op: trialData[2]},
		};
	} else {
		// if problemType === "multiplication" || problemType === "division" ?
		partialTrial = {
			response: {response: parseInt(trialData[2][0])},
			problemData: {factors: trialData.slice(0, 2)},
		};
	}

	return _.extend({}, backendTrial, partialTrial, {
		outcome: problem.customState.currentOutcome,
		final: true,
		attemptNumber: 1,
	});
};

const getStars = function(result) {
	const currentProblem = result.problems[0];
	return currentProblem.customState.stars;
};

const getPercentComplete = function(result) {
	const currentProblem = result.problems[0];
	return Math.min(
		1,
		Math.max(
			currentProblem.customState.percentFilled,
			1 -
				currentProblem.customState.numProblemsLeft /
					currentProblem.customState.totalProblems
		)
	);
};

const canGoToNextProblem = function(result) {
	return true;
};

const getTrialXP = function(result) {
	return 1;
};

const isCurrentProblemDone = function(result) {
	return isFinished(result);
};

const isFinished = function(result) {
	const currentProblem = result.problems[0];
	//End if they've reached 3 stars or ran out of problems and the last problem's reaction has occurred.
	return (
		currentProblem.customState.stars === 3 ||
		(currentProblem.customState.numProblemsLeft === 0 &&
			currentProblem.customState.numReactions ===
				currentProblem.customState.totalProblems)
	);
};

const getResultProgressXP = function(backendResult) {
	const {progress} = backendResult;
	const numCorrect = _.get(progress, "problems.0.customState.numCorrect") || 0;
	return numCorrect * getTrialXP();
};

const processPostSubmission = function(result, processor) {
	const currentProblem = result.problems[0];
	const problemModel = currentProblem.model;
	const customState = currentProblem.customState;
	const newCustomState = processor.updateCustomState(
		problemModel,
		customState,
		currentProblem.trials,
		currentProblem.lastTrialTimeSpent
	);
	return setIn(result, ["problems", 0, "customState"], newCustomState);
};

const getSummaryProblemsSolvedText = function(result) {
	const currentProblem = result.problems[0];
	const customState = currentProblem.customState;
	const {
		gameType,
		problemType,
		numCorrect,
		totalProblems,
		numProblemsLeft,
		trialTimes,
	} = customState;
	const numProblems = totalProblems - numProblemsLeft;
	const accuracy = _.round((100 * numCorrect) / numProblems);

	let summary = ["Correct: " + accuracy + "%"];

	if (gameType === "estimation") {
		if (
			problemType === "compareToZero" ||
			problemType === "divisionEstimation"
		) {
			summary = [
				`You answered ${numCorrect} out of ${numProblems} problems correctly.`,
			];
		}
	} else {
		const avgTime = _.round(_.mean(trialTimes) / 1000, 1);
		const totalTime = _.round(_.sum(trialTimes) / 1000);
		summary = summary.concat([
			"Average time: " + avgTime + " seconds",
			"Total time: " + totalTime + " seconds",
		]);
	}

	return summary;
};

const getStarProgressInfo = function(result) {
	const starCutoffs = [1, 2 / 3, 1 / 3];
	return {
		styleType: result.styleType,
		filledAmount: (getStars(result) * 1) / 3,
		totalAmount: 1,
		allCutoffs: starCutoffs,
		starCutoffs,
		keepStarsReached: true,
	};
};

const getSummaryForReport = function(backendResult) {
	const customState = _.get(backendResult, "progress.problems[0].customState");
	if (!customState) {
		return "Problems cannot be displayed for this game lesson.";
	} else {
		return `${customState.numCorrect || 0} / ${customState.numReactions ||
			0} problems answered correctly.`;
	}
};

module.exports = {
	settings,
	computeBackendProgress,
	computeBackendTrial,
	initialize,
	isCurrentProblemDone,
	canGoToNextProblem,
	getStars,
	getPercentComplete,
	isFinished,
	getResultProgressXP,
	processPostSubmission,
	getTrialXP,
	getSummaryProblemsSolvedText,
	getStarProgressInfo,
	getSummaryForReport,
};
